/**
 * Os filtros da tela de Faturas: os chips de status, os de forma de pagamento e o que conta como vencida.
 * Fora do componente para que a CONSERVAÇÃO possa ser provada em teste: nenhuma fatura pode ficar
 * inalcançável por nenhum chip (filtrosDasFaturas.test.ts).
 */

export interface FaturaFiltravel {
  status?: string;
  paymentMethod?: string | null;
  dueDate?: string | null;
}

/** Vencida não é status: é cálculo. Aberta ou Parcial com o vencimento já passado (fim do dia). */
export function vencida(inv: FaturaFiltravel, agora: Date = new Date()): boolean {
  if (inv.status !== 'Aberta' && inv.status !== 'Parcial') return false;
  if (!inv.dueDate) return false;
  return new Date(String(inv.dueDate).split('T')[0] + 'T23:59:59') < agora;
}

/** O que entra na contagem e no filtro de "vencidas" dos cartões: Aberta OU Parcial, vencida. */
export function entraEmVencidas(inv: FaturaFiltravel, agora: Date = new Date()): boolean {
  return vencida(inv, agora);
}

/**
 * Os cartões de resumo que filtram por status (o de e-mail pendente depende do cadastro do cliente e dos
 * logs de envio, e fica no componente; ele é um subconjunto de Aberta).
 */
export type CartaoDeStatus = 'paid' | 'open' | 'overdue' | 'draft';
export function passaNoCartao(inv: FaturaFiltravel, cartao: CartaoDeStatus, agora: Date = new Date()): boolean {
  switch (cartao) {
    case 'paid': return inv.status === 'Paga';
    case 'open': return (inv.status === 'Aberta' || inv.status === 'Parcial') && !vencida(inv, agora);
    case 'overdue': return entraEmVencidas(inv, agora);
    case 'draft': return inv.status === 'Rascunho';
  }
}

/**
 * Um chip por STATUS do backend, e só status. Cada fatura cai em exatamente um (a soma dos chips é o
 * "Todos"). "Vencida" saiu daqui (06/10/2026): não é status, é cálculo, e o chip pegava só Aberta vencida
 * enquanto o cartão pegava Aberta OU Parcial vencida, dois conjuntos diferentes com o mesmo nome. As
 * vencidas moram na entrada "Vencidas" do cartão "Precisa de você". "Parcial" entrou: era o único status
 * sem chip, alcançável só por "Todos" e pelos cartões.
 */
export const CHIPS_DE_STATUS: { key: string; label: string }[] = [
  { key: 'Todos', label: 'Todos' },
  { key: 'Paga', label: 'Paga' },
  { key: 'Aberta', label: 'Aberta' },
  { key: 'Parcial', label: 'Parcial' },
  { key: 'Rascunho', label: 'Rascunho' },
  { key: 'Cancelada', label: 'Cancelada' },
];

export function passaNoChipDeStatus(inv: FaturaFiltravel, key: string, _agora: Date = new Date()): boolean {
  return key === 'Todos' || inv.status === key;
}

/** Chave do chip das faturas sem forma de pagamento (null ou vazio). Não colide com nenhum valor gravado. */
export const SEM_FORMA = '__sem_forma';

/**
 * A forma de pagamento como chave de chip: sem caixa e sem espaço em volta. Medido em produção em
 * 06/10/2026: existe `TED` maiúsculo (1 fatura) ao lado de `ted` (285), e com igualdade estrita aquela
 * fatura ficava fora de todos os chips.
 */
export function formaDePagamento(inv: FaturaFiltravel): string {
  return String(inv.paymentMethod ?? '').trim().toLowerCase() || SEM_FORMA;
}

const ROTULO_DA_FORMA: Record<string, string> = {
  boleto: 'Boleto', pix: 'PIX', ted: 'TED', transferencia: 'Transferência', deposito: 'Depósito', outros: 'Outros',
  [SEM_FORMA]: 'Sem forma',
};
const ORDEM_DA_FORMA = ['boleto', 'pix', 'ted', 'transferencia', 'deposito', 'outros'];

/**
 * Os chips de forma de pagamento saem das FATURAS, não de uma lista fixa: o comentário da entity diz
 * `boleto|pix|ted|deposito` e erra nos dois sentidos (medido em 06/10/2026: `deposito` tem zero linhas,
 * e `outros`, `transferencia` e null existem). Lista fixa deixava fatura sem chip; esta não deixa e não
 * inventa chip para valor que não aparece. Valor desconhecido vira chip com o próprio valor gravado.
 * '' é "Todos Pgto".
 */
export function chipsDePagamento(faturas: FaturaFiltravel[], ativo = ''): { key: string; label: string }[] {
  const presentes = Array.from(new Set(faturas.map(formaDePagamento)));
  // O chip que está filtrando continua na tela mesmo sem fatura no período (contagem 0): sumir com ele
  // deixaria a lista filtrada por um chip que ninguém vê nem consegue desligar.
  if (ativo && !presentes.includes(ativo)) presentes.push(ativo);
  const ordem = (k: string) => (k === SEM_FORMA ? 1000 : ORDEM_DA_FORMA.indexOf(k) === -1 ? 500 : ORDEM_DA_FORMA.indexOf(k));
  presentes.sort((a, b) => ordem(a) - ordem(b) || a.localeCompare(b));
  return [{ key: '', label: 'Todos Pgto' }, ...presentes.map((k) => ({ key: k, label: ROTULO_DA_FORMA[k] ?? k }))];
}

export function passaNoChipDePagamento(inv: FaturaFiltravel, key: string): boolean {
  return !key || formaDePagamento(inv) === key;
}
